import { useState } from "react";
import { X, Calendar, Clock, Users, Phone, Mail, NotebookPen, CheckCircle2, CalendarDays, ExternalLink } from "lucide-react";
import { AddToWalletButtons } from "./AddToWalletButtons";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function BookTableModal({ restaurant, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:00");
  const [partySize, setPartySize] = useState(2);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await fetch(
        `${API}/api/directory/restaurants/${restaurant.slug}/book`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name, phone, email, date, time,
            party_size: partySize, notes,
          }),
        },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Could not book");
      setSuccess(data.booking);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar URLs — routed through the unified `/api/calendar/{kind}/{id}`
  // endpoint so admin pass-settings gates work + audit trail is consistent
  // across all bookings/tickets. See `hp_calendar_pass.py`.
  const icsUrl = success ? `${API}/api/calendar/booking/${success.id}.ics` : "";
  const gcalUrl = success ? `${API}/api/calendar/booking/${success.id}/google` : "";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div
        data-testid="book-table-modal"
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <h2 className="font-serif text-xl text-stone-900">Book a table</h2>
            <p className="text-[12px] text-stone-500">at {restaurant.name}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="book-modal-close"
            className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h3 className="font-serif text-2xl text-stone-900 mb-2">You're booked!</h3>
            <p className="text-stone-600 text-[14px] mb-4">
              {restaurant.name} will contact you on {success.phone} to confirm.
            </p>
            <div className="bg-stone-50 rounded-lg p-4 text-left text-[13px] space-y-1 mb-4">
              <div><span className="text-stone-400">When: </span><span className="font-semibold">{success.date} at {success.time}</span></div>
              <div><span className="text-stone-400">Party: </span>{success.party_size} guests</div>
              <div><span className="text-stone-400">Booking ID: </span><span className="font-mono text-[11px]">{success.id.slice(0, 8)}</span></div>
            </div>

            {/* Add-to-calendar buttons — routed through the unified
                /api/calendar/{kind}/{id} endpoint (see hp_calendar_pass.py). */}
            <div className="space-y-2 mb-3">
              <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Add to calendar</p>
              <a
                data-testid="download-ics-btn"
                href={icsUrl}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-300 text-stone-700 font-semibold text-[14px] flex items-center justify-center gap-2 hover:bg-stone-50"
              >
                <CalendarDays className="w-4 h-4" />
                Apple / Outlook (.ics)
              </a>
              <a
                data-testid="google-cal-btn"
                href={gcalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-300 text-stone-700 font-semibold text-[14px] flex items-center justify-center gap-2 hover:bg-stone-50"
              >
                <Calendar className="w-4 h-4" />
                Google Calendar
                <ExternalLink className="w-3 h-3 opacity-50" />
              </a>
            </div>

            {/* Add-to-wallet buttons — universal component wired to
                `hp_wallet_universal._booking_spec`. Renders nothing
                if the admin has disabled wallet for this user's tier
                or if PassKit/GoogleWallet aren't configured (component
                checks /wallet-pass/availability). QR in the pass opens
                the venue-side ``/api/reservations/{id}/scan/{token}``
                URL — one scan flips the booking to ``seated``. */}
            <div className="space-y-2 mb-3">
              <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Save to wallet</p>
              <AddToWalletButtons
                kind="booking"
                itemId={success.id}
                className="flex flex-col gap-2"
              />
            </div>

            <button onClick={onClose} className="w-full px-4 py-3 rounded-xl bg-stone-900 text-white font-semibold">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3 overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Date</span>
                <input data-testid="book-date" required type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Clock className="w-3 h-3" /> Time</span>
                <input data-testid="book-time" required type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400" />
              </label>
            </div>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Users className="w-3 h-3" /> Party size</span>
              <div className="mt-1 grid grid-cols-6 gap-1.5">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <button type="button" key={n} onClick={() => setPartySize(n)}
                    data-testid={`book-party-${n}`}
                    className={`py-2.5 rounded-lg border text-[14px] font-semibold transition-colors ${
                      partySize === n ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
                    }`}>{n}</button>
                ))}
              </div>
              {partySize === 6 && (
                <input type="number" min="6" max="40" value={partySize} onChange={(e) => setPartySize(Number(e.target.value))}
                  className="w-full mt-2 px-3 py-2 rounded-lg border border-stone-200 text-[14px]" placeholder="More than 6? Enter total" />
              )}
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Your name</span>
              <input data-testid="book-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="First & last name"
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400" />
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Phone className="w-3 h-3" /> Phone</span>
              <input data-testid="book-phone" required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0400 000 000"
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400" />
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email (optional)</span>
              <input data-testid="book-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="for confirmation"
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400" />
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1.5"><NotebookPen className="w-3 h-3" /> Notes</span>
              <textarea data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows="2"
                placeholder="Allergies, occasions, window seat…"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400" />
            </label>

            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 text-[13px] rounded-lg px-3 py-2">{error}</div>
            )}

            <button data-testid="book-submit" type="submit" disabled={submitting}
              className="w-full px-4 py-3 rounded-xl bg-stone-900 text-white font-semibold disabled:opacity-50">
              {submitting ? "Booking…" : "Confirm booking"}
            </button>
            <p className="text-[11px] text-stone-400 text-center">Demo only — no SMS confirmation is sent.</p>
          </form>
        )}
      </div>
    </div>
  );
}
